import { serverTimestamp } from "firebase/firestore";
import { TalkOgp } from "./TalkOgp";
import { TALK_DATE_FORMAT, TALK_THREAD_ID_NONE } from "~/consts/TalkConfig";

// メッセージタイプ
export type MessageType = "text" | "date" | "pdf" | "image" | "loading";

export enum DeleteType {
  none = 0,
  delete = 1,
  systemDelete = 9,
}

// トークデータ
export class Talk {
  public talkId: string;
  public talkRoomId: string; // firestoreには無いデータ。検索結果などビュー用に設定される
  public messageType: MessageType;
  public contents: string;
  public contentsUrl: string;
  public fileName: string;
  public fileKey: string | undefined; // firestoreには無いデータ。一斉配信の一部処理でファイルのキーとして使う
  public thumbnailImage: string; // サムネイル画像 (旧 base64)
  public thumbnailImageUrl: string; // サムネイル画像 (署名付きURL)
  public userId: string;
  public deleteType: DeleteType;
  public postDateTime: Date | null;
  public updateDateTime: Date | null;
  public note: string | undefined;
  public mentionUserIds: string[] | undefined;
  public replyTalkId: string | undefined;
  public goodCount: number;

  public showInRoom: boolean; // トークルームに表示するかどうか
  public threadTalkId: string; // スレッドの親トークID
  public threadComments: number;
  public threadUserIds: string[] | undefined;
  public threadLatestTalkId: string; // スレッドの最新トークID
  public threadLatestDate: Date | null; // スレッドの最新トーク日時

  public bookmarked: boolean; // firestoreの別コレクションから取得したデータ

  // firestoreには無いデータ
  public ogpLoading: boolean;
  public ogpList: TalkOgp[];
  // eslint-disable-next-line no-use-before-define
  public replyTalk: Talk | undefined;
  // eslint-disable-next-line no-use-before-define
  public threadTalk: Talk | undefined;
  public date: string | undefined; // 日付表示用

  constructor(talkId: string, options: any = {}) {
    this.talkId = talkId;
    this.talkRoomId = options?.talkRoomId ?? "";
    this.messageType = options?.messageType ?? "text";
    this.contents = options?.contents ?? "";
    this.contentsUrl = options?.contentsUrl ?? "";
    this.fileName = options?.fileName ?? "";
    this.fileKey = options?.fileKey ?? undefined;
    this.thumbnailImage = options?.thumbnailImage ?? "";
    this.thumbnailImageUrl = options?.thumbnailImageUrl ?? "";
    this.userId = options?.userId ?? "";
    this.deleteType = options?.deleteType ?? 0;
    this.postDateTime = options?.postDateTime ?? new Date();
    this.updateDateTime = options?.updateDateTime ?? null;
    this.note = options?.note ?? undefined;
    this.mentionUserIds = options?.mentionUserIds ?? undefined;
    this.replyTalkId = options?.replyTalkId ?? undefined;
    this.goodCount = options?.goodCount ?? 0;
    this.showInRoom = !!options?.showInRoom;
    this.threadTalkId = options?.threadTalkId ?? "0";
    this.threadComments = options?.threadComments ?? 0;
    this.threadUserIds = options?.threadUserIds ?? undefined;
    this.threadLatestTalkId = options?.threadLatestTalkId ?? "0";
    this.threadLatestDate = options?.threadLatestDate ?? null;
    this.bookmarked = !!options?.bookmarked;
    this.ogpLoading = !!options?.ogpLoading;
    this.ogpList = options?.ogpList ?? [];
    this.replyTalk = options?.replyTalk ?? undefined;
    this.threadTalk = options?.threadTalk ?? undefined;
    this.date = options?.date ?? undefined;
  }

  isDeleted() {
    return this.deleteType !== DeleteType.none;
  }

  canView() {
    // トークが未削除 or スレッドコメントが1件以上ある場合に表示可能
    return this.deleteType === DeleteType.none || this.threadComments > 0;
  }

  hideFromView() {
    // 論理削除して表示されないようにする
    this.deleteType = DeleteType.delete;
    this.threadComments = 0; // スレッドコメント数が1以上で表示するケースもあるので
  }

  isThreadParent() {
    return this.threadTalkId === TALK_THREAD_ID_NONE;
  }

  isThreadChild() {
    return !this.isThreadParent();
  }

  getPath(planId: string) {
    const linkBase = `/plan/${planId}?tab=talk&room=${this.talkRoomId}`;
    return this.isThreadChild()
      ? `${linkBase}&thread=${this.threadTalkId}&talk=${this.talkId}`
      : `${linkBase}&talk=${this.talkId}`;
  }

  getThreadPath(planId: string) {
    return `/plan/${planId}?tab=talk&room=${this.talkRoomId}&thread=${this.talkId}`;
  }

  isSameDate(talk: Talk) {
    if (!this.postDateTime || !talk.postDateTime) {
      return false;
    }
    return (
      FormatDateString(this.postDateTime, TALK_DATE_FORMAT) === FormatDateString(talk.postDateTime, TALK_DATE_FORMAT)
    );
  }
}

// Firestore data converter
export const TalkConverter = {
  toFirestore: (item: any) => {
    return {
      TalkId: item.talkId,
      Type: item.messageType,
      Message: item.contents,
      contentsUrl: item.contentsUrl,
      fileName: item.fileName,
      thumbnailImage: item.thumbnailImage,
      thumbnailImageUrl: item.thumbnailImageUrl,
      UserId: item.userId,
      deleteType: item.deleteType,
      InsertDate: item.postDateTime ?? serverTimestamp(),
      UpdateDate: item.updateDateTime ?? serverTimestamp(),
      note: item.note,
      mentionUserIds: item.mentionUserIds,
      replyTalkId: item.replyTalkId,
      GoodCount: item.goodCount,
    };
  },
  fromFirestore: (snapshot: any, options: any) => {
    const data = snapshot.data(options);
    return new Talk(String(data.TalkId), {
      messageType: data.Type,
      contents: data.Message,
      contentsUrl: data.contentsUrl,
      fileName: data.fileName,
      thumbnailImage: data.thumbnailImage,
      thumbnailImageUrl: data.thumbnailImageUrl,
      userId: data.UserId,
      deleteType: data.deleteType,
      postDateTime: data.InsertDate?.toDate(),
      updateDateTime: data.UpdateDate?.toDate(),
      note: data.note || undefined,
      mentionUserIds: data.mentionUserIds || undefined,
      replyTalkId: data.replyTalkId ? String(data.replyTalkId) : undefined,
      goodCount: data.GoodCount,
      showInRoom: data.showInRoom,
      threadTalkId: data.threadTalkId ? String(data.threadTalkId) : "0",
      threadComments: data.threadComments,
      threadUserIds: data.threadUserIds,
      threadLatestTalkId: data.threadLatestTalkId ? String(data.threadLatestTalkId) : "0",
      threadLatestDate: data.threadLatestDate?.toDate(),
    });
  },
};

export const newLoadingTalk = (talkId: string) =>
  new Talk(talkId, {
    messageType: "loading",
    postDateTime: new Date(0),
  });

export const newDateTalk = (dateTime: Date) => {
  const date = FormatDateString(dateTime, TALK_DATE_FORMAT);
  const dateTalk: any = {
    talkId: date,
    messageType: "date",
    date: date,
  } as Talk;
  return dateTalk;
};

export const copyTalk = (fromTalk: Talk, toTalk: Talk) => {
  // 可変要素のみコピー
  toTalk.deleteType = fromTalk.deleteType;
  toTalk.contents = fromTalk.contents;
  toTalk.note = fromTalk.note;
  toTalk.mentionUserIds = fromTalk.mentionUserIds;
  toTalk.updateDateTime = fromTalk.updateDateTime;
  toTalk.goodCount = fromTalk.goodCount;
  toTalk.bookmarked = fromTalk.bookmarked;
  toTalk.ogpList = fromTalk.ogpList;
  toTalk.replyTalk = fromTalk.replyTalk;
  toTalk.threadTalk = fromTalk.threadTalk;
  toTalk.showInRoom = fromTalk.showInRoom;
  toTalk.threadComments = fromTalk.threadComments;
  toTalk.threadUserIds = fromTalk.threadUserIds;
  toTalk.threadLatestTalkId = fromTalk.threadLatestTalkId;
  toTalk.threadLatestDate = fromTalk.threadLatestDate;
};
